import React, { useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { ControllerChips, ControllerEditor, ControllerTextInput } from "components/basic/controller-inputs/ControllerInputs";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { MaintenanceDto, MaintenanceEmail, MaintenanceStatus, useGetCoordinatedMaintenanceByProjectIdQuery, useTestEmailLazyQuery } from "gql/generated/types";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Controller, useForm } from "react-hook-form";
import { T } from "services/lang/LanguageService";
import { v4 } from "uuid";
import { z } from "zod";
import { Dialog } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import useProjectContext from "../../hooks/useProjectContext";
import { maintenanceStatuses } from "../../utils/const";
import { statuses } from "../../utils/types";
import { apolloErrorToaster, errorToaster, renderTag } from "../../utils/utils";
import EmailTextInfo from "./EmailTextInfo";

type MaintenanceEmailFormProps = {
  setEmails: React.Dispatch<React.SetStateAction<MaintenanceEmail[]>>;
  email?: MaintenanceEmail;
  closeDialog?: () => void;
};

const schema = z.object({
  title: z.string({ message: T("word.required-fields") }),
  triggerFrom: z.enum(statuses),
  triggerTo: z.enum(statuses),
  header: z.string({ message: T("word.required-fields") }),
  recipientsTo: z
    .array(
      z.string({ message: T("word.required-fields") }).email({ message: T("alert.invalid-email") }),
      { message: T("word.required-fields") }
    )
    .nonempty({ message: T("word.required-fields") }),
  recipientsCc: z.optional(z.array(z.string({ message: T("word.required-fields") }).email())),
  text: z.string({ message: T("word.required-fields") }),
});

const MaintenanceEmailForm: React.FC<MaintenanceEmailFormProps> = ({ setEmails, email, closeDialog }) => {
  const [maintenance, setMaintenance] = useState<MaintenanceDto | null>(null);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<MaintenanceEmail>({
    defaultValues: email,
    resolver: zodResolver(schema),
  });

  const { projectId } = useProjectContext();

  const {data: coordData, loading} = useGetCoordinatedMaintenanceByProjectIdQuery({
    variables: {
      id: projectId!
    },
    skip: !projectId,
    onError: apolloErrorToaster
  })

  const [test, {loading: testLoading}] = useTestEmailLazyQuery();

  const coordMaintenances = useMemo(() => coordData?.coordinatedByProjectId ?? [], [coordData?.coordinatedByProjectId]);

  const onSubmit = (data: MaintenanceEmail) => {
    if (email) {
      setEmails(prev => prev.map(e => (e.id === email.id ? { ...email, ...data } : e)));
    } else {
      setEmails(prev => [...prev, {...data, id: v4()}]);
    }
    closeDialog?.();
  };

  const sendTestEmail = (data: MaintenanceEmail) => {
    if (!maintenance) {
      errorToaster(T("alert.coordinated-maintenance-not-selected"));
      return;
    }
    test({
      variables: {
        maintenance,
        email: {...data, id: v4()}
      }
    })
  }

  return (
    <form className="flex flex-column maintenance-email-form" onSubmit={handleSubmit(onSubmit)}>
      <TabView>
        <TabPanel header={T("word.main")} contentStyle={{ minHeight: "40rem" }}>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <ControllerTextInput field={field} errors={errors} label={T("word.title")} col={12} />
            )}
          />
          <div className="flex flex-wrap">
            <InputLabelWrapper labelName={T("word.email-trigger")} col={6}>
              <div className="p-inputgroup">
                <Controller
                  name="triggerFrom"
                  defaultValue={MaintenanceStatus.Coordinated}
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      value={field.value}
                      options={maintenanceStatuses}
                      optionLabel="translationKey"
                      onChange={e => field.onChange(e.value)}
                      itemTemplate={opt => renderTag({ status: opt.value })}
                      valueTemplate={opt => opt && renderTag({ status: opt.value })}
                      className="status-dropdown"
                    />
                  )}
                />
                <span className="p-inputgroup-addon p-1">⟶</span>
                <Controller
                  name="triggerTo"
                  defaultValue={MaintenanceStatus.Planned}
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      value={field.value}
                      options={maintenanceStatuses}
                      optionLabel="translationKey"
                      onChange={e => field.onChange(e.value)}
                      itemTemplate={opt => renderTag({ status: opt.value })}
                      valueTemplate={opt => opt && renderTag({ status: opt.value })}
                      className="status-dropdown"
                    />
                  )}
                />
              </div>
            </InputLabelWrapper>
            <Controller
              name="header"
              control={control}
              render={({ field }) => (
                <ControllerTextInput
                  field={field}
                  errors={errors}
                  label={T("word.header")}
                  col={6}
                />
              )}
            />
          </div>
          <Controller
            name="recipientsTo"
            defaultValue={[]}
            control={control}
            render={({ field }) => (
              <ControllerChips
                field={field}
                errors={errors}
                label={`${T("word.recipients")} [To]`}
                col={12}
                separator=", "
              />
            )}
          />
          <Controller
            name="recipientsCc"
            defaultValue={[]}
            control={control}
            render={({ field }) => (
              <ControllerChips
                field={field}
                errors={errors}
                label={`${T("word.recipients")} [Cc]`}
                col={12}
              />
            )}
          />
        </TabPanel>
        <TabPanel header={T("word.template")} contentStyle={{ minHeight: "40rem" }}>
          <Controller
            name="text"
            control={control}
            render={({ field }) => (
              <ControllerEditor
                field={field}
                errors={errors}
                label={
                  <>
                    {T("word.text")}
                    <i
                      className="ml-2 cursor-pointer pi pi-info-circle"
                      onClick={() => setOpenInfoDialog(true)}
                    />
                  </>
                }
                col={12}
              />
            )}
          />
        </TabPanel>
      </TabView>
      <div className="flex justify-content-between mt-3">
        <div className="p-inputgroup w-25rem">
          <Dropdown
            value={maintenance}
            options={coordMaintenances}
            optionLabel="title"
            placeholder={T("word.select-one")}
            emptyMessage={T("mdm.coordinated-maintenance-not-found")}
            onChange={e => setMaintenance(e.value)}
          />
          <Button
            label={T("label.test-email")}
            title={T("message.testing-email-for-coordinated-maintenance")}
            className="p-inputgroup-addon"
            onClick={handleSubmit(sendTestEmail)}
            loading={loading || testLoading}
            severity="warning"
          />
        </div>
        <Button
          label={email ? T("action.update") : T("action.add")}
          severity={email ? "info" : "success"}
        />
      </div>
      <Dialog
        header={T("mdm.maintenance-model")}
        visible={openInfoDialog}
        onHide={() => setOpenInfoDialog(false)}
      >
        <EmailTextInfo />
      </Dialog>
    </form>
  );
};

export default MaintenanceEmailForm;
